import { ReactNode, useEffect } from 'react'

import { Box, Container, Flex } from '@chakra-ui/react'

import { EmployeeHeader } from '@core/components/EmployeeHeader'
import { FeedbackModal } from '@core/components/FeedbackModal'
import { Footer } from '@core/components/Footer/Footer'
import { TermsAndPoliciesModal } from '@core/components/TermsAndPoliciesModal'
import { FeedbackModalProvider } from '@core/utils/contexts/FeedbackModalContext'
import { Sidebar } from '../Sidebar'

interface Props {
    children: ReactNode
    useContainer?: boolean
    showFooter?: boolean
    showHeader?: boolean
    showSidebar?: boolean
    showTermsAndPoliciesModal?: boolean
    headerTitle?: string
    bgColor?: string
    isEmployeeHeaderFixed?: boolean
    lockViewport?: boolean
}

export const EmployeeLayout = ({
    children,
    bgColor,
    useContainer = true,
    showFooter = true,
    showHeader = true,
    showSidebar = false,
    showTermsAndPoliciesModal = true,
    headerTitle,
    isEmployeeHeaderFixed = false,
    lockViewport = false,
}: Props): JSX.Element => {
    const backgroundColor = bgColor ?? '#EDEEF0'

    useEffect(() => {
        if (!lockViewport) return

        const html = document.documentElement
        const { body } = document
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            htmlOverscroll: html.style.overscrollBehavior,
            bodyOverscroll: body.style.overscrollBehavior,
        }

        html.style.overflow = 'hidden'
        body.style.overflow = 'hidden'
        html.style.overscrollBehavior = 'none'
        body.style.overscrollBehavior = 'none'

        return () => {
            html.style.overflow = prev.htmlOverflow
            body.style.overflow = prev.bodyOverflow
            html.style.overscrollBehavior = prev.htmlOverscroll
            body.style.overscrollBehavior = prev.bodyOverscroll
        }
    }, [lockViewport])

    return (
        <FeedbackModalProvider>
            <Flex
                minH={lockViewport ? '100dvh' : '100vh'}
                h={lockViewport ? '100dvh' : undefined}
                overflow={lockViewport ? 'hidden' : undefined}
                overscrollBehavior={lockViewport ? 'none' : undefined}
                flexDirection={'column'}
                id={'app-container'}
            >
                {showHeader && <EmployeeHeader isFixed={isEmployeeHeaderFixed} title={headerTitle} />}
                <Box
                    as={'main'}
                    flex={1}
                    minH={0}
                    h={lockViewport ? undefined : 'full'}
                    overflow={lockViewport ? 'hidden' : undefined}
                    backgroundColor={backgroundColor}
                    display={'flex'}
                >
                    {showSidebar && <Sidebar />}
                    {useContainer ? (
                        <Container
                            maxW={{
                                base: 'container.sm',
                                xl: 'container.xl',
                            }}
                        >
                            {children}
                        </Container>
                    ) : (
                        children
                    )}
                </Box>
                {showFooter && <Footer />}
            </Flex>
            <FeedbackModal />
            {showTermsAndPoliciesModal && <TermsAndPoliciesModal />}
        </FeedbackModalProvider>
    )
}
